import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, HeartPulse, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex bg-blue-950 text-white relative overflow-hidden p-14 flex-col justify-between">
        <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-white text-blue-900 grid place-items-center">
            <HeartPulse className="w-5 h-5" />
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-lg text-white font-semibold">Ambigara</span>
            <span className="block text-[11px] uppercase tracking-[0.18em] text-blue-200 font-medium">College of Nursing</span>
          </span>
        </Link>

        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-rose-300 mb-4">Admin Portal</p>
          <h2 className="font-heading text-4xl xl:text-5xl leading-tight text-white">
            Manage inquiries, admissions and prospective students in one place.
          </h2>
          <p className="text-blue-100 mt-6 max-w-md leading-relaxed">
            Secure access for administration staff to review incoming applications and coordinate follow-ups.
          </p>
        </div>

        <div className="relative text-xs text-blue-200">
          © {new Date().getFullYear()} Ambigara Chowdaiah College of Nursing
        </div>
      </div>

      {/* Right panel form */}
      <div className="flex items-center justify-center p-6 sm:p-14">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <span className="w-11 h-11 rounded-xl bg-blue-900 text-white grid place-items-center">
              <HeartPulse className="w-5 h-5" />
            </span>
            <span className="font-heading text-lg font-semibold text-slate-900">Ambigara</span>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-rose-500 mb-3">Sign In</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">
            Welcome back, <span className="italic">Admin</span>
          </h1>
          <p className="text-slate-500 mt-3">Enter your credentials to access the dashboard.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="al-email" className="text-slate-700 text-sm">Email</Label>
              <Input
                id="al-email"
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ambigara.com"
                className="mt-1.5 h-12 rounded-xl border-slate-200 focus:border-blue-900 focus:ring-blue-900/20"
                required
              />
            </div>
            <div>
              <Label htmlFor="al-password" className="text-slate-700 text-sm">Password</Label>
              <Input
                id="al-password"
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-12 rounded-xl border-slate-200 focus:border-blue-900 focus:ring-blue-900/20"
                required
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-blue-900 hover:bg-blue-800 text-white font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-transform"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              Sign In
            </Button>
          </form>

          <Link to="/" data-testid="admin-back-home" className="mt-8 inline-block text-sm text-slate-500 hover:text-blue-900 transition-colors">
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
